import { type ReceivingSession, ReceivingSessionSchema } from '../data/receiving';
import type { FetchWrapper } from '../helpers/fetch-wrapper';
import { createMultipleHandler, createSingleHandler } from '../helpers/fetch-wrapper';

const baseUrl = `${import.meta.env.VITE_SERVER_URL}/stores`;

// For bodyless POSTs (cancel/resume/abandon/check-file-status): reject on a
// non-2xx response, preferring the backend's {"message": "..."} body so screens
// can show actionable copy — e.g. the 409 "companion is not connected" from
// /resume and /abandon. Swallowing these turned clicks into silent no-ops.
async function ensureOk(res: Response, action: string): Promise<void> {
  if (res.ok) {
    return;
  }

  let message = `${action} failed: ${res.status} ${res.statusText}`;
  try {
    const body = (await res.json()) as { message?: string };
    if (body.message) {
      message = body.message;
    }
  } catch {
    // Non-JSON error body — keep the status-line message.
  }

  throw new Error(message);
}

export type ReceivingIntake = 'paste' | 'file';

export interface LeaseResult {
  granted: boolean;
  current_holder?: string;
}

export interface PresignedUploadTarget {
  url: string;
  s3_key: string;
  fields?: Record<string, string>;
}

export class ReceivingService {
  constructor(
    private readonly fetch: FetchWrapper,
    private readonly tabId: string
  ) {}

  listActiveSessions(storeId: string): Promise<ReceivingSession[]> {
    return this.fetch
      .get(`${baseUrl}/${storeId}/receiving-sessions`)
      .then(createMultipleHandler(ReceivingSessionSchema));
  }

  getSession(storeId: string, sessionId: string): Promise<ReceivingSession> {
    return this.fetch
      .get(`${baseUrl}/${storeId}/receiving-sessions/${sessionId}`)
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  startSession(storeId: string): Promise<ReceivingSession> {
    return this.fetch
      .post(`${baseUrl}/${storeId}/receiving-sessions`, {}, { headers: { 'X-Tab-ID': this.tabId } })
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  setCompanion(storeId: string, sessionId: string, machineId: string): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/companion`,
        { machine_id: machineId },
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  claimSession(storeId: string, sessionId: string): Promise<LeaseResult> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/claim`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(async (res) => {
        // Allow 409 (lease denied) — it still has a valid JSON body
        if (!res.ok && res.status !== 409) {
          throw new Error(`claimSession failed: ${res.status} ${res.statusText}`);
        }

        return (await res.json()) as LeaseResult;
      });
  }

  setVendor(storeId: string, sessionId: string, vendorId: string): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/set-vendor`,
        { vendor_id: vendorId },
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  async uploadFile(storeId: string, sessionId: string, file: File): Promise<void> {
    const sha256 = await this.computeSHA256(file);
    const target = await this.getPresignedUpload(storeId, sessionId, file, sha256);
    await this.putToS3(target, file);
  }

  processInvoices(
    storeId: string,
    sessionId: string,
    intake: ReceivingIntake
  ): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/process?intake=${intake}`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  /**
   * Confirm the current review results and trigger LPOS file write.
   * No body is sent — the backend reads the LPOS blob from the stored
   * ProcessingResult.ReceivingFile.Content (Phase A design decision in !3339).
   */
  confirm(storeId: string, sessionId: string): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/confirm`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  retry(storeId: string, sessionId: string): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/retry`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  cancel(storeId: string, sessionId: string): Promise<void> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/cancel`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then((res) => ensureOk(res, 'cancel'));
  }

  // POST /resume — triggers the backend to push session_resume_probe to the
  // companion. 409 when the companion is offline (the probe is not replayed on
  // reconnect, so the user must retry once it's back).
  resume(storeId: string, sessionId: string): Promise<void> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/resume`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then((res) => ensureOk(res, 'resume'));
  }

  // POST /abandon — initiates the clear_rcv_file handshake; backend resolves
  // on rcv_clear_result and transitions the session or refuses (busy:true).
  // 409 when the companion is offline (the clear can't be delivered).
  abandon(storeId: string, sessionId: string): Promise<void> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/abandon`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then((res) => ensureOk(res, 'abandon'));
  }

  requestFileStatusCheck(storeId: string, sessionId: string): Promise<void> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/check-file-status`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then((res) => ensureOk(res, 'file-status check'));
  }

  private async computeSHA256(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buffer);

    return btoa(String.fromCharCode(...new Uint8Array(digest)));
  }

  private async getPresignedUpload(
    storeId: string,
    sessionId: string,
    file: File,
    sha256: string
  ): Promise<PresignedUploadTarget> {
    const res = await this.fetch.post(
      `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/upload-url`,
      { filename: file.name, content_type: file.type, sha256, size: file.size },
      { headers: { 'X-Tab-ID': this.tabId } }
    );
    if (!res.ok) {
      throw new Error(`getPresignedUpload failed: ${res.status} ${res.statusText}`);
    }

    return (await res.json()) as PresignedUploadTarget;
  }

  private async putToS3(target: PresignedUploadTarget, file: File): Promise<void> {
    // The presigned URL already embeds the SHA-256 checksum as a query parameter
    // (X-Amz-Checksum-Sha256). Sending it again as an x-amz-* header makes S3
    // reject with 403 — "duplicate x-amz-* in query string and header".
    const res = await fetch(target.url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!res.ok) {
      throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
    }
  }
}
